import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { VideoPlayer as VideoPlayerComponent } from '../components/common';
import { FiPlus, FiTrendingUp, FiRefreshCw, FiTarget, FiClock, FiAward } from 'react-icons/fi';
import { api } from '../services/api';

const PageContainer = styled.div`
  background: #eaf4eb;
  min-height: 100vh;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  justify-content: center;
  gap: 4rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem 3rem 2rem;
  background: #eaf4eb;
  position: relative;
  overflow: hidden;

  /* Background image with overlay */
  background-image: 
    linear-gradient(
      rgba(234, 244, 235, 0.95), 
      rgba(234, 244, 235, 0.9)
    ),
    url('/Background Image 1.webp');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    padding: 3.5rem 2rem 2.5rem 2rem;
    background-position: center 30%;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    padding: 3rem 2rem 2rem 2rem;
    background-position: center 25%;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 2.5rem;
    align-items: center;
    padding: 2.5rem 2rem 2rem 2rem;
  }

  @media (max-width: 600px) {
    padding: 2rem 1.5rem 1.5rem 1.5rem;
    gap: 2rem;
    background-position: center 20%;
  }
`;

const HeroLeft = styled.div`
  flex: 1 1 420px;
  min-width: 260px;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2rem 1rem;

  @media (max-width: 900px) {
    max-width: 100%;
    min-width: 0;
    padding: 1rem 0;
    width: 100%;
  }

  @media (max-width: 600px) {
    padding: 0.5rem 0;
  }
`;

const HeroTitle = styled.h1`
  font-size: 2.2rem;
  font-weight: 800;
  color: #222;
  margin-bottom: 2rem;
  line-height: 1.18;
  letter-spacing: -0.01em;

  @media (max-width: 900px) {
    font-size: 1.8rem;
    text-align: center;
    margin-bottom: 1.5rem;
    line-height: 1.22;
  }

  @media (max-width: 600px) {
    font-size: 1.5rem;
    line-height: 1.22;
    margin-bottom: 1.2rem;
  }
`;

const HeroDesc = styled.p`
  font-size: 1.2rem;
  color: #444;
  margin-bottom: 1.5rem;
  font-weight: 400;
  line-height: 1.7;
  letter-spacing: 0.01em;

  @media (max-width: 900px) {
    font-size: 1.1rem;
    line-height: 1.7;
    margin-bottom: 1.2rem;
    text-align: center;
  }

  @media (max-width: 600px) {
    font-size: 1rem;
    line-height: 1.7;
    margin-bottom: 1rem;
  }
`;

const HeroSubDesc = styled.div`
  font-size: 1.1rem;
  color: #444;
  margin-bottom: 0;
  line-height: 1.6;
  font-weight: 500;

  @media (max-width: 900px) {
    font-size: 1rem;
    line-height: 1.6;
    text-align: center;
  }

  @media (max-width: 600px) {
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const HeroBenefits = styled.div`
  flex: 1 1 400px;
  min-width: 220px;
  max-width: 500px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  align-items: flex-start;
  padding: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 1.2rem;
    max-width: 100%;
    padding: 0.5rem;
  }

  @media (max-width: 600px) {
    gap: 1rem;
    padding: 0;
  }
`;

const BenefitCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: #fff;
  border-radius: 1.2rem;
  border: 1.5px solid #7ac142;
  box-shadow: 0 4px 16px rgba(122, 193, 66, 0.10);
  padding: 1.5rem 1rem 1.2rem 1rem;
  min-height: 120px;
  transition: box-shadow 0.2s, transform 0.2s, border-color 0.2s;

  &:hover {
    box-shadow: 0 10px 28px rgba(122, 193, 66, 0.18), 0 4px 12px rgba(0, 0, 0, 0.10);
    border-color: #5ea32e;
    transform: translateY(-2px) scale(1.02);
  }

  @media (max-width: 900px) {
    padding: 1.2rem 1.5rem 1rem 1.5rem;
    font-size: 0.98rem;
    width: 100%;
    min-height: 100px;
  }

  @media (max-width: 600px) {
    padding: 1rem 1.2rem 0.8rem 1.2rem;
    font-size: 0.95rem;
    min-height: 90px;
  }
`;

const BenefitIcon = styled.div`
  margin-bottom: 0.5rem;
  color: #7ac142;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 2rem;
    height: 2rem;
    stroke: #7ac142;
    fill: none;
    stroke-width: 2.2;
  }
`;

const BenefitTitle = styled.div`
  font-size: 1rem;
  color: #222;
  font-weight: 600;
  margin-bottom: 0;
`;

const CTASection = styled.section`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 4rem;
  max-width: 1200px;
  margin: 2rem auto;
  padding: 4rem 2rem 3rem 2rem;
  background: #eaf4eb;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 2.5rem;
    align-items: center;
    padding: 2.5rem 2rem 2rem 2rem;
    margin: 1.5rem auto;
  }

  @media (max-width: 600px) {
    padding: 2rem 1.5rem 1.5rem 1.5rem;
    gap: 2rem;
    margin: 1rem auto;
  }
`;

const CTAImage = styled.div`
  flex: 1 1 420px;
  min-width: 220px;
  max-width: 520px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    max-width: 100%;
    border-radius: 1.2rem;
    box-shadow: 0 4px 24px rgba(122, 193, 66, 0.10);
    object-fit: cover;
    filter: brightness(0.96) saturate(1.08) hue-rotate(-4deg);
    aspect-ratio: 4/3;
    height: auto;
    display: block;
    margin: 0 auto;
  }
`;

const CTAContent = styled.div`
  flex: 1 1 420px;
  min-width: 220px;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 1rem;

  @media (max-width: 900px) {
    max-width: 100%;
    min-width: 0;
    padding: 0.5rem 0;
    width: 100%;
  }

  @media (max-width: 600px) {
    padding: 0;
  }
`;

const CTATitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 800;
  color: #222;
  margin-bottom: 1.5rem;
  line-height: 1.13;

  @media (max-width: 900px) {
    font-size: 1.5rem;
    text-align: center;
    margin-bottom: 1.2rem;
  }

  @media (max-width: 600px) {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }
`;

const CTADesc = styled.div`
  font-size: 1.1rem;
  color: #444;
  margin-bottom: 1.5rem;
  line-height: 1.7;

  .highlight {
    color: #7ac142;
    font-weight: 600;
  }

  @media (max-width: 600px) {
    font-size: 1rem;
    margin-bottom: 1.2rem;
  }
`;

const CTAButton = styled(Link)`
  background: #7ac142;
  color: #fff;
  font-size: 1.2rem;
  font-weight: 700;
  border: none;
  border-radius: 2rem;
  padding: 1.2rem 2.5rem;
  margin-top: 1rem;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(122, 193, 66, 0.15);
  transition: all 0.3s ease;
  text-decoration: none;
  display: block;
  text-align: center;

  &:hover {
    background: #5ea32e;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(122, 193, 66, 0.25);
  }

  @media (max-width: 900px) {
    width: 100%;
    font-size: 1.1rem;
    padding: 1.1rem 2rem;
    margin-top: 1rem;
  }

  @media (max-width: 600px) {
    font-size: 1rem;
    padding: 1rem 1.5rem;
    margin-top: 0.8rem;
  }
`;

const IndustriesSection = styled.section`
  max-width: 1100px;
  margin: 3rem auto 4rem auto;
  padding: 2rem;

  @media (max-width: 900px) {
    padding: 1.5rem;
    width: 100%;
    margin: 2rem auto 3rem auto;
  }

  @media (max-width: 600px) {
    margin: 1.5rem auto 2rem auto;
    padding: 1rem;
  }
`;

const IndustriesTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #222;
  margin-bottom: 2rem;
  text-align: center;

  @media (max-width: 900px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 600px) {
    font-size: 1.3rem;
    margin-bottom: 1.2rem;
  }
`;

const IndustriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  align-items: stretch;
  justify-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  @media (max-width: 600px) {
    gap: 1.2rem;
  }
`;

const IndustryCard = styled.div`
  background: #eaf4eb;
  border-radius: 1.2rem;
  border: 1.5px solid #7ac142;
  box-shadow: 0 4px 16px rgba(122, 193, 66, 0.10);
  padding: 2rem 1.5rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-height: 140px;
  transition: box-shadow 0.2s, transform 0.2s, border-color 0.2s;
  width: 100%;

  &:hover {
    box-shadow: 0 10px 28px rgba(122, 193, 66, 0.18), 0 4px 12px rgba(0, 0, 0, 0.10);
    border-color: #5ea32e;
    transform: translateY(-2px) scale(1.02);
  }

  @media (max-width: 900px) {
    padding: 1.5rem 1.2rem 1.2rem 1.2rem;
    width: 100%;
    min-height: 120px;
  }

  @media (max-width: 600px) {
    padding: 1.2rem 1rem 1rem 1rem;
    min-height: 100px;
  }
`;

const IndustryTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  color: #222;
  margin-bottom: 0.8rem;

  @media (max-width: 600px) {
    font-size: 1.1rem;
    margin-bottom: 0.6rem;
  }
`;

const IndustryDesc = styled.p`
  font-size: 1rem;
  color: #444;
  line-height: 1.6;
  margin: 0;

  @media (max-width: 600px) {
    font-size: 0.95rem;
    line-height: 1.5;
  }
`;

const HowRPLWorksPage = () => {
  const benefits = [
    {
      icon: <FiPlus />,
      title: "Save time and money"
    },
    {
      icon: <FiTrendingUp />,
      title: "Improved career prospects"
    },
    {
      icon: <FiRefreshCw />,
      title: "Avoiding the repetition of previous training"
    },
    {
      icon: <FiTarget />,
      title: "Continuous professional development"
    },
    {
      icon: <FiClock />,
      title: "Help with immigration and visa requirements"
    },
    {
      icon: <FiAward />,
      title: "Meeting relevant industry & licensing requirements"
    }
  ];

  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/categories');
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    fetchCategories();
  }, []);

  return (
    <PageContainer>
      {/* Hero Section */}
      <HeroSection>
        <HeroLeft>
          <HeroTitle>How Recognition of Prior Learning (RPL) can work for you</HeroTitle>
          <HeroDesc>
            Working with RPL Support to have your skills recognised through RPL can have a wide range of benefits for your career and lifestyle.
          </HeroDesc>
          <HeroSubDesc>
            Achieving a nationally recognised qualification through RPL Support can have multiple benefits, including:
          </HeroSubDesc>
        </HeroLeft>
        <HeroBenefits>
          {benefits.map((benefit, index) => (
            <BenefitCard key={index}>
              <BenefitIcon>
                {benefit.icon}
              </BenefitIcon>
              <BenefitTitle>{benefit.title}</BenefitTitle>
            </BenefitCard>
          ))}
        </HeroBenefits>
      </HeroSection>

      {/* CTA Section */}
      <CTASection>
        <CTAImage>
          <img 
            src="/pexels-rpl-works.jpeg" 
            alt="Aged care: young woman helping elderly lady at home" 
          />
        </CTAImage>
        <CTAContent>
          <CTATitle>Start your RPL journey today and never look back!</CTATitle>
          <CTADesc>
            Having your skills recognised through <span className="highlight">Recognition of Prior Learning (RPL)</span> may have a wide range of benefits for your career and lifestyle.
            <br /><br />
            An initial discussion could help you achieve a nationally recognised qualification through RPL Support and our partner RTOs.
            <br /><br />
            There are so many benefits to having your skills recognised including increased career opportunities, avoiding repeated training and much more.
          </CTADesc>
          <CTAButton to="/free-quote">
            Take The FREE 60 Second Skills Check Now!!
          </CTAButton>
        </CTAContent>
      </CTASection>

      {/* Industries Section */}
      <IndustriesSection>
        <IndustriesTitle>Industries & Qualifications</IndustriesTitle>
        <IndustriesGrid>
          {categories.map((category, index) => (
            <IndustryCard key={index}>
              <IndustryTitle>{category.name}</IndustryTitle>
              <IndustryDesc>{category.description}</IndustryDesc>
            </IndustryCard>
          ))}
        </IndustriesGrid>
      </IndustriesSection>

      {/* Video Section */}
      <VideoPlayerComponent
        videoSrc="/video3.mp4"
        title="How RPL Works"
        subtitle="Watch our comprehensive guide to understand the Recognition of Prior Learning process and how it can benefit your career."
        description="This video walks you through the complete RPL process, from initial assessment to qualification achievement, helping you understand how your experience can be converted into formal recognition."
      />
    </PageContainer>
  );
};

export default HowRPLWorksPage; 